import { useState, useRef, useMemo, useCallback } from "react";
import { useAudit } from "../../../../contexts/auditContext";
import { Input } from "../../../../components/ui/input";
import { cn } from "../../../../lib/utils";
import { Button } from "../../../../components/ui/button";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { getFileInputAcceptString } from "../../../../lib/fileTypes";
import { validateFilesWithToast } from "../../../../lib/fileUploadValidation";
import { ProcedureSessionSelectionModal } from "./ProcedureSessionSelectionModal";
import { usePresignedUpload } from "../../../../hooks/usePresignedUpload";
import { UploadProgressOverlay } from "../../../../components/UploadProgressOverlay";
import { UploadSourcePopover } from "./UploadSourcePopover";
import { HoverTooltip } from "../../../../components/HoverTooltip";
import { ExternalFilesImportDialog } from "./ExternalFilesImportDialog";
import {
  ExternalFile,
  useExternalFiles,
} from "../../../../hooks/useExternalFiles";

const GENERAL_EVIDENCE_TOOLTIP =
  "Evidence that applies to every sample. Midship attaches the files to all of them.";

export const AuditUploadGuidanceDoc = () => {
  const {
    uploadGuidanceDocumentsToControlSessionProcedures,
    controlSession,
    notifyDocumentUploadStarted,
  } = useAudit();
  const {
    state: presignedUploadState,
    uploadFiles: uploadFilesPresigned,
    cancel: cancelPresignedUpload,
    reset: resetPresignedUpload,
  } = usePresignedUpload();
  const {
    fetchExternalFiles,
    downloadFile,
    transferGuidanceFilesToProcedures,
    files: externalFiles,
    loading: externalFilesLoading,
    downloading: externalDownloading,
  } = useExternalFiles({
    controlSessionId: controlSession?.id ?? null,
    notifyDocumentUploadStarted,
  });
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [pendingExternalFiles, setPendingExternalFiles] = useState<
    ExternalFile[]
  >([]);
  const [showExternalDialog, setShowExternalDialog] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const externalFilesRequestedForControlIdRef = useRef<string | null>(null);
  const externalFilesLoadedForControlIdRef = useRef<string | null>(null);

  const controlId = controlSession?.control?.id ?? null;
  const externalRef = controlSession?.control?.externalRef ?? null;
  const hasExternalIntegration = Boolean(externalRef);

  const enabledProcedureSessions = useMemo(() => {
    if (!controlSession) return [];
    return controlSession.procedureSessions
      .filter((ps) => ps.procedure.isEnabled)
      .sort(
        (a, b) =>
          (a.procedure.procedureOrder || 0) - (b.procedure.procedureOrder || 0),
      );
  }, [controlSession]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) {
      toast.error("No file selected");
      return;
    }
    const files = Array.from(e.target.files).filter((file) => {
      const fileName = file.name.toLowerCase();
      return !fileName.endsWith(".url");
    });

    if (files.length === 0) {
      toast.error("Please select valid files (URL files are not allowed)");
      return;
    }

    const validFiles = await validateFilesWithToast(files);

    if (validFiles.length === 0) return;

    // If only one procedure session, auto-select it and skip modal
    if (enabledProcedureSessions.length === 1) {
      await handleModalConfirm([enabledProcedureSessions[0].id], validFiles);
    } else {
      // Open modal for procedure session selection
      setPendingFiles(validFiles);
      setShowModal(true);
    }
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleModalConfirm = async (
    selectedProcedureSessionIds: string[],
    filesToUpload?: File[],
  ) => {
    // If we have pending external files, dispatch the transfer instead
    if (pendingExternalFiles.length > 0) {
      const filesToTransfer = pendingExternalFiles;
      setPendingExternalFiles([]);
      setShowModal(false);
      try {
        await transferGuidanceFilesToProcedures(
          filesToTransfer,
          selectedProcedureSessionIds,
        );
      } catch (error) {
        console.error("Error transferring guidance external files:", error);
      }
      return;
    }

    const files = filesToUpload ?? pendingFiles;
    if (files.length === 0) return;

    setUploading(true);
    try {
      // Try presigned upload first (for large files)
      const result = await uploadFilesPresigned(files);

      if (result.usedPresigned) {
        // Presigned upload was used - finalize with upload IDs
        await uploadGuidanceDocumentsToControlSessionProcedures(
          files,
          selectedProcedureSessionIds,
          result.uploadIds,
        );
        resetPresignedUpload();
      } else {
        // Small files - use existing multipart flow
        await uploadGuidanceDocumentsToControlSessionProcedures(
          files,
          selectedProcedureSessionIds,
        );
      }

      // Reset the input value to allow selecting the same file again
      if (inputRef.current) {
        inputRef.current.value = "";
      }
      setPendingFiles([]);
    } catch (error) {
      // Don't show error toast for cancelled uploads
      if (error instanceof DOMException && error.name === "AbortError") {
        resetPresignedUpload();
        return;
      }
      console.error("Error uploading guidance documents:", error);
      toast.error("Error uploading guidance documents");
    } finally {
      setUploading(false);
    }
  };

  const uploadFiles = async (files: File[]) => {
    // If only one procedure session, auto-select it and skip modal
    if (enabledProcedureSessions.length === 1) {
      await handleModalConfirm([enabledProcedureSessions[0].id], files);
    } else {
      // Open modal for procedure session selection
      setPendingFiles(files);
      setShowModal(true);
    }
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      await uploadFiles(files);
    }
  };

  const triggerComputerFilePicker = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const ensureExternalFilesLoaded = useCallback(async () => {
    if (!controlId) return;
    if (
      externalFilesLoadedForControlIdRef.current === controlId ||
      externalFilesRequestedForControlIdRef.current === controlId
    ) {
      return;
    }
    externalFilesRequestedForControlIdRef.current = controlId;
    const result = await fetchExternalFiles(controlId);
    if (result) {
      externalFilesLoadedForControlIdRef.current = controlId;
    }
    externalFilesRequestedForControlIdRef.current = null;
  }, [controlId, fetchExternalFiles]);

  const handleOpenOptroDialog = useCallback(() => {
    setShowExternalDialog(true);
    void ensureExternalFilesLoaded();
  }, [ensureExternalFilesLoaded]);

  const handlePopoverOpenChange = useCallback(
    (open: boolean) => {
      if (open && hasExternalIntegration) {
        void ensureExternalFilesLoaded();
      }
    },
    [ensureExternalFilesLoaded, hasExternalIntegration],
  );

  const handleExternalImport = useCallback(
    async (selected: ExternalFile[]) => {
      if (selected.length === 0) return;
      if (enabledProcedureSessions.length === 0) {
        toast.warning("No enabled procedures to import into");
        return;
      }
      if (enabledProcedureSessions.length === 1) {
        await transferGuidanceFilesToProcedures(selected, [
          enabledProcedureSessions[0].id,
        ]);
        return;
      }
      // Multiple procedure sessions: defer to the picker
      setPendingExternalFiles(selected);
      setShowModal(true);
    },
    [enabledProcedureSessions, transferGuidanceFilesToProcedures],
  );

  const hasSourceRowsInSession = useMemo(() => {
    return (
      controlSession?.procedureSessions.some((ps) => ps.sourceRowsCount > 0) ??
      false
    );
  }, [controlSession]);

  const disabled = uploading || !hasSourceRowsInSession;

  const triggerButton = (
    <Button
      disabled={disabled}
      variant="outline"
      size="default"
      className={cn(isDragging ? "border-stone-400 bg-stone-100" : "")}
    >
      <Plus size={16} className="mr-2" />
      {uploading ? "Uploading..." : "General Evidence"}
    </Button>
  );

  const procedureSessionModalDocumentNames = useMemo(() => {
    if (pendingExternalFiles.length > 0) {
      return pendingExternalFiles.map((f) => f.name ?? `File ${f.id}`);
    }
    return pendingFiles.map((file) => file.name);
  }, [pendingExternalFiles, pendingFiles]);

  const procedureSessionModalDocumentCount =
    pendingExternalFiles.length > 0
      ? pendingExternalFiles.length
      : pendingFiles.length;

  return (
    <div className="w-fit">
      <div
        className={cn(
          "relative transition-colors duration-200 h-10",
          isDragging ? "bg-stone-100 rounded-lg" : "",
        )}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {hasExternalIntegration ? (
          <UploadSourcePopover
            trigger={triggerButton}
            tooltip={GENERAL_EVIDENCE_TOOLTIP}
            onUploadFromComputer={triggerComputerFilePicker}
            onImportFromOptro={handleOpenOptroDialog}
            onOpenChange={handlePopoverOpenChange}
          />
        ) : (
          <HoverTooltip content={GENERAL_EVIDENCE_TOOLTIP}>
            <div
              role="button"
              tabIndex={disabled ? -1 : 0}
              onClick={() => {
                if (!disabled) triggerComputerFilePicker();
              }}
              onKeyDown={(e) => {
                if (disabled) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  triggerComputerFilePicker();
                }
              }}
            >
              {triggerButton}
            </div>
          </HoverTooltip>
        )}
        <Input
          ref={inputRef}
          id="audit-upload-guidance-doc"
          type="file"
          accept={getFileInputAcceptString()}
          multiple
          onChange={handleFileSelect}
          disabled={!hasSourceRowsInSession}
          className="sr-only"
        />
      </div>
      <ProcedureSessionSelectionModal
        open={showModal}
        onOpenChange={(open) => {
          setShowModal(open);
          if (!open) {
            setPendingExternalFiles([]);
          }
        }}
        onConfirm={handleModalConfirm}
        documentCount={procedureSessionModalDocumentCount}
        documentNames={procedureSessionModalDocumentNames}
      />
      <ExternalFilesImportDialog
        open={showExternalDialog}
        onOpenChange={setShowExternalDialog}
        mode="guidance"
        files={externalFiles}
        loading={externalFilesLoading}
        downloading={externalDownloading}
        onDownload={downloadFile}
        onImport={handleExternalImport}
      />
      <UploadProgressOverlay
        state={presignedUploadState}
        onCancel={cancelPresignedUpload}
      />
    </div>
  );
};
